import type { IconGlyph } from '@sunwebgroup/uds-ui/icon'
import type * as React from 'react'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { useIntl } from '@/intl'
import { useBookingDraftQuery } from '../../domain/hooks'
import { CheckoutSection } from '../CheckoutSection'
import styles from './Transfer.module.css'

const AIRPORT_STEP_KEYS = [
  'checkout_confirmation_transfer_airport_step_1',
  'checkout_confirmation_transfer_airport_step_2',
  'checkout_confirmation_transfer_airport_step_3',
] as const

function Transfer() {
  const intl = useIntl()
  const { data } = useBookingDraftQuery()

  const transferService = data?.confirmation?.services.find(service => service.serviceAName?.toUpperCase() === 'TRANSFER')
  if (!transferService) {
    return null
  }

  const isPrivateTransfer = transferService.lines.some(line => line.code?.startsWith('PT'))
  const departureDate = data?.confirmation?.departureDate
  const formattedDepartureDate = departureDate ? departureDate.toLocaleDateString(intl.locale, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }) : undefined

  return (
    <CheckoutSection
      glyph={isPrivateTransfer ? 'CarRight' : 'BusRight'}
      title={intl.formatMessage({ id: isPrivateTransfer ? 'checkout_confirmation_transfer_private_title' : 'checkout_confirmation_transfer_title' })}
      description={intl.formatMessage({ id: 'checkout_confirmation_transfer_description' })}
    >
      <div className={styles.cards}>
        <div className={styles.card}>
          <div className={styles.header}>
            <Typography as="h3" variant="display-i-regular">
              {intl.formatMessage({ id: 'checkout_confirmation_transfer_airport_title' })}
            </Typography>
            {formattedDepartureDate && (
              <Typography as="p" variant="body-s-regular-tight" className={styles.date}>
                {formattedDepartureDate}
              </Typography>
            )}
          </div>
          <div className={styles.body}>
            <ol className={styles.steps}>
              {AIRPORT_STEP_KEYS.map((id, index) => (
                <li key={id} className={styles.step}>
                  <span className={styles.stepNumber} aria-hidden="true">
                    <Typography as="span" variant="body-s-bold-tight">{index + 1}</Typography>
                  </span>
                  <Typography as="span" variant="body-m-regular-tight">
                    {intl.formatMessage({ id })}
                  </Typography>
                </li>
              ))}
            </ol>
          </div>
        </div>

        <div className={styles.card}>
          <div className={styles.header}>
            <Typography as="h3" variant="display-i-regular">
              {intl.formatMessage({ id: 'checkout_confirmation_transfer_hotel_title' })}
            </Typography>
          </div>
          <div className={styles.body}>
            <div className={styles.content}>
              <Typography as="p" variant="body-m-regular-tight" className={styles.fallback}>
                {intl.formatMessage({ id: 'checkout_confirmation_transfer_hotel_fallback' })}
              </Typography>
            </div>
            <Remark glyph="InformationInCircle">
              {intl.formatMessage({ id: 'checkout_confirmation_transfer_disclaimer' })}
            </Remark>
          </div>
        </div>
      </div>
    </CheckoutSection>
  )
}

function Remark({ glyph, children }: React.PropsWithChildren<{ glyph: IconGlyph }>) {
  return (
    <div className={styles.remark}>
      <Icon glyph={glyph} size={16} className={styles.remarkIcon} />
      <Typography as="div" variant="body-s-regular-tight">{children}</Typography>
    </div>
  )
}

export default Transfer
